import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GOOGLE_ADS_MARKET_CHANNEL,
  marketHomeGoogleAdsKeyValues
} from '../../../utils/adsUtils';
import { checkIsAuthenticated } from '../../../utils/authUtils';
import { MARKET_HOME_META } from '../../../utils/meta';
import useMobileDetect from '../../hooks/useMobileDetect';
import { getMarketHomeAdsAsync } from '../../store/slices/common/adsSlice';
import { setHighlightItem } from '../../store/slices/common/commonSlice';
import {
  getLatestAdvertisementsAsync,
  getMarketPostsAsync,
} from '../../store/slices/market/marketSlice';

// Convert all component imports to dynamic imports
const BrunoAd = dynamic(() => import('../../components/Common/BrunoAd'));
const ContainerWithAd = dynamic(
  () => import('../../components/Common/ContainerWithAd/ContainerWithAd')
);
const ForYouSection = dynamic(
  () => import('../../components/Common/ForYouSection/ForYouSection'),
  {
    loading: () => <div className='min-h-[577px]'></div>,
  }
);
const SpaceDivider = dynamic(
  () => import('../../components/Common/SpaceDivider/SpaceDivider')
);
const GoogleAdBannerHorizontal = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerHorizontal')
);
const GoogleAdBannerVertical2 = dynamic(
  () => import('../../components/GoogleAd/GoogleAdBannerVertical2')
);
const EventSection = dynamic(
  () => import('../../components/Market/EventSection/EventSection'),
  {
    loading: () => <div className='min-h-[499px]'></div>,
  }
);
const CityOfferCard = dynamic(
  () => import('../../components/Market/MarketCard/CityOfferCard'),
  {
    loading: () => <div className='min-h-[499px]'></div>,
  }
);
const HousingCard = dynamic(
  () => import('../../components/Market/MarketCard/HousingCard'),
  {
    loading: () => <div className='min-h-[499px]'></div>,
  }
);
const JobsCard = dynamic(
  () => import('../../components/Market/MarketCard/JobsCard'),
  {
    loading: () => <div className='min-h-[499px]'></div>,
  }
);
const MarketCard = dynamic(
  () => import('../../components/Market/MarketCard/MarketCard'),
  {
    loading: () => <div className='min-h-[499px]'></div>,
  }
);
const SecondHandCard = dynamic(
  () => import('../../components/Market/MarketCard/SecondHandCard'),
  {
    loading: () => <div className='min-h-[499px]'></div>,
  }
);
const MarketSectionContainer = dynamic(
  () =>
    import(
      '../../components/Market/MarketSectionContainer/MarketSectionContainer'
    ),
  {
    loading: () => <div className='min-h-[577px]'></div>,
  }
);
const SearchField = dynamic(
  () => import('../../components/Market/SearchField/SearchField'),
  {
    loading: () => <div className='min-h-[48px]'></div>,
  }
);
const PageContainer = dynamic(
  () => import('../../components/PageContainer/PageContainer')
);
// const CategorySectionSkeleton = dynamic(
//   () => import('../../components/SkeletonLoaders/CategorySectionSkeleton')
// );
// const EventSectionSkeleton = dynamic(
//   () => import('../../components/SkeletonLoaders/EventSectionSkeleton')
// );

export async function getServerSideProps({ query }) {
  const { city } = query;
  return {
    props: {
      metaData: MARKET_HOME_META(city),
    },
  };
}

export default function MarketPage() {
  const lang = useSelector(({ common }) => common?.language);
  const router = useRouter();
  const { city, tag_filter = 'most_popular' } = router.query;
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [isPostDeleted, setIsPostDeleted] = useState(false);
  const isMobile = useMobileDetect();
  const latestAdvertisements = useSelector(
    ({ market }) => market?.latestAdvertisements
  );

  const selectedCity = useSelector(({ cities }) => cities?.selectedCity);

  const marketHomeAds = useSelector(({ ads }) => {
    return ads.marketHomeAds;
  });

  useEffect(() => {
    dispatch(getMarketPostsAsync({})).then((res) => {
      if (res?.meta?.requestStatus === 'fulfilled') {
        setData(res?.payload);
      }
    });
    setIsPostDeleted(false);
  }, [isPostDeleted, selectedCity?.id]);

  useEffect(() => {
    dispatch(getLatestAdvertisementsAsync());
    setIsPostDeleted(false);
  }, [isPostDeleted]);

  useEffect(() => {
    dispatch(setHighlightItem('Home'));
  }, []);
  useEffect(() => {
    if (isMobile && selectedCity?.id) {
      dispatch(getMarketHomeAdsAsync({ cityId: selectedCity?.id }));
    }
  }, [isMobile, selectedCity?.id]);

  return (
    <PageContainer showFooter={data !== null}>
      <ContainerWithAd
        ad={
          <GoogleAdBannerVertical2
            channel={GOOGLE_ADS_MARKET_CHANNEL}
            keyValues={marketHomeGoogleAdsKeyValues}
          />
        }
      >
        {latestAdvertisements && latestAdvertisements?.length > 0 && (
          <div className='w-full bg-primary px-[24px] py-[32px]'>
            <h1 className='mb-[4px] text-[28px] font-semibold leading-[35.2px] text-white  md:text-[36px]'>
              {lang?.market?.welcome_to_market || 'Welcome to Market'}
            </h1>
            <h2 className='mb-[24px] text-[12px] leading-[19.2px] text-white md:text-[16px]'>
              {lang?.market?.welcome_description ||
                'For each ad, you will find the appropriate category here. Do you think something could be improved or a category is missing? Then head over to the «Ronsters» section under «Ask Switzerland» in the topic «Tell Ron». I’ll take a look – and yes, I’ll eventually link it too.'}
            </h2>
            <SearchField />
          </div>
        )}

        {latestAdvertisements && latestAdvertisements?.length > 0 && (
          <MarketSectionContainer
            title={lang?.market?.latest_advertisement}
            noPlaceAd
            withoutSeeAll
          >
            {latestAdvertisements.map((item, index) => (
              <MarketCard
                key={index}
                data={item}
                setIsPostDeleted={setIsPostDeleted}
              />
            ))}
          </MarketSectionContainer>
        )}
        {isMobile && marketHomeAds?.[0] && (
          <div className='my-[24px] flex justify-center md:justify-start'>
            <BrunoAd data={marketHomeAds?.[0]} />
          </div>
        )}
        {data ? (
          <>
            {data && data?.housing?.length > 0 && (
              <MarketSectionContainer
                title={lang?.market?.housing}
                placeAdLink={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/housing?city=${city}`}
                link={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/housing?city=${city}&tag_filter=${tag_filter}`}
              >
                {data?.housing?.map((item, index) => (
                  <HousingCard
                    key={index}
                    data={item}
                    setIsPostDeleted={setIsPostDeleted}
                  />
                ))}
              </MarketSectionContainer>
            )}
            <div className='my-[30px] flex justify-center'>
              <GoogleAdBannerHorizontal
                id='adslot_horizontal_1'
                keyValues={marketHomeGoogleAdsKeyValues}
                channel={GOOGLE_ADS_MARKET_CHANNEL}
                pos={1}
              />
            </div>
            {data && data?.events?.length > 0 && (
              <EventSection data={data?.events} />
            )}

            {isMobile && marketHomeAds?.[1] && (
              <div className='my-[24px] flex justify-center md:justify-start'>
                <BrunoAd data={marketHomeAds?.[1]} />
              </div>
            )}

            {data && data?.secondhand?.length > 0 && (
              <MarketSectionContainer
                title={lang?.market?.second_hand}
                placeAdLink={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/secondhand?city=${city}`}
                link={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/secondhand?city=${city}&tag_filter=${tag_filter}`}
              >
                {data?.secondhand?.map((item, index) => (
                  <SecondHandCard
                    key={index}
                    data={item}
                    setIsPostDeleted={setIsPostDeleted}
                  />
                ))}
              </MarketSectionContainer>
            )}
            <div className='my-[30px] flex justify-center'>
              <GoogleAdBannerHorizontal
                id='adslot_horizontal_2'
                keyValues={marketHomeGoogleAdsKeyValues}
                channel={GOOGLE_ADS_MARKET_CHANNEL}
                pos={2}
              />
            </div>
            {data && data?.jobs?.length > 0 && (
              <MarketSectionContainer
                placeAdLink={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/jobs?city=${city}`}
                link={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/jobs?city=${city}&tag_filter=${tag_filter}`}
                title={lang?.market?.jobs}
              >
                {data?.jobs?.map((item, index) => (
                  <JobsCard
                    key={index}
                    data={item}
                    setIsPostDeleted={setIsPostDeleted}
                  />
                ))}
              </MarketSectionContainer>
            )}
            {isMobile && marketHomeAds?.[2] && (
              <div className='my-[24px] flex justify-center md:justify-start'>
                <BrunoAd data={marketHomeAds?.[2]} />
              </div>
            )}
            {data && data?.cityoffer?.length > 0 && (
              <MarketSectionContainer
                placeAdLink={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/place-ad/cityoffer?city=${city}`}
                link={`${process.env.NEXT_PUBLIC_FRONTEND_URL}/market/cityoffer?city=${city}&tag_filter=${tag_filter}`}
                title={lang?.market?.city_offer}
              >
                {data?.cityoffer?.map((item, index) => (
                  <CityOfferCard
                    key={index}
                    data={item}
                    setIsPostDeleted={setIsPostDeleted}
                  />
                ))}
              </MarketSectionContainer>
            )}
          </>
        ) : (
          // <div className='my-8 flex flex-col gap-12'>
          //   <CategorySectionSkeleton />
          //   <CategorySectionSkeleton />
          //   <EventSectionSkeleton />
          //   <CategorySectionSkeleton />
          //   <CategorySectionSkeleton />
          //   <CategorySectionSkeleton />
          //   <CategorySectionSkeleton />
          // </div>
          <></>
        )}

        {checkIsAuthenticated() && <ForYouSection isHomePage />}
        <SpaceDivider />
      </ContainerWithAd>
    </PageContainer>
  );
}
